import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  effect,
  inject,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentFileLink } from '@interfaces/accounting-data-standardization/responses/document-file-link.response';
import { TranslateService } from '@ngx-translate/core';
import { DataRoomHttpService } from '@services/http/data-room/data-room-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { DocumentOriginalViewerComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/document-original-viewer/document-original-viewer.component';
import { DataRoomPdfViewerComponent } from '@features/dashboard/market/data-room/components/data-room-pdf-viewer/data-room-pdf-viewer.component';
import {
  CharterDocumentRequest,
  quoteSearchText,
} from '@features/dashboard/company/corporate-charter/corporate-charter-provenance.util';
import { CharterDocumentViewerService } from '@features/dashboard/company/corporate-charter/services/charter-document-viewer.service';

/**
 * Documento original de um valor do Societário, aberto na página onde o valor está escrito. Reaproveita o visualizador
 * de PDF do Data Room (`app-data-room-pdf-viewer`, pdf.js com camada de texto e busca) com `initialPage` e destaca o
 * começo do trecho literal; imagem e outros formatos usam o visualizador da Padronização. O link do arquivo é pedido
 * na hora (SAS de poucos minutos), como no Data Room.
 *
 * Montado uma vez na página; abre a cada pedido do `CharterDocumentViewerService`.
 */
@Component({
  selector: 'app-charter-document-viewer',
  imports: [SharedModule, DataRoomPdfViewerComponent, DocumentOriginalViewerComponent],
  templateUrl: './charter-document-viewer.component.html',
  styleUrl: './charter-document-viewer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterDocumentViewerComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  private readonly requests = inject(CharterDocumentViewerService);
  private readonly http = inject(DataRoomHttpService);
  private readonly destroyRef = inject(DestroyRef);

  /** "Ver no Data Room": a página navega para a pasta do documento com o painel de detalhes aberto. */
  readonly openInDataRoom = output<string>();

  protected readonly current = signal<CharterDocumentRequest | null>(null);
  protected readonly link = signal<DocumentFileLink | null>(null);
  protected readonly loading = signal(false);
  protected readonly failed = signal(false);

  protected readonly isPdf = computed(() => {
    const link = this.link();
    const ext = (link?.fileExtension || extensionOf(link?.fileName) || '').toLowerCase();
    return ext === 'pdf';
  });

  protected readonly searchText = computed(() => quoteSearchText(this.current()?.quote));

  private token = 0;

  constructor(translateService: TranslateService) {
    super(translateService);
    effect(() => {
      const request = this.requests.request();
      if (request) {
        untracked(() => this.open(request));
      }
    });
  }

  /** Abre o diálogo e pede o link do arquivo. */
  open(request: CharterDocumentRequest): void {
    const token = ++this.token;
    this.current.set(request);
    this.link.set(null);
    this.failed.set(false);
    this.loading.set(true);
    this.dialogRef?.open('', '', '', 'Fechar', '', 'charter-document-viewer', true, false);
    this.http
      .getDownloadLinks([request.documentId])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: links => {
          if (token !== this.token) return;
          const url = links?.[0] ?? null;
          this.loading.set(false);
          if (!url) {
            this.failed.set(true);
            return;
          }
          const fileName = request.documentName ?? 'documento';
          this.link.set({ url, fileName, fileExtension: extensionOf(fileName) ?? '' });
        },
        error: () => {
          if (token !== this.token) return;
          this.loading.set(false);
          this.failed.set(true);
        },
      });
  }

  protected download(): void {
    const url = this.link()?.url;
    if (url) {
      window.open(url, '_blank', 'noopener');
    }
  }

  protected goToDataRoom(): void {
    const request = this.current();
    if (request) {
      this.dialogRef?.close();
      this.openInDataRoom.emit(request.documentId);
    }
  }
}

function extensionOf(fileName: string | null | undefined): string | null {
  const match = /\.([a-z0-9]+)$/i.exec(fileName ?? '');
  return match ? match[1] : null;
}
